/**
 * EquipmentPriceBook
 * ------------------
 * The price book behind every notes-to-quote BOM. Key cost and sell once —
 * RO skids, tanks, boosters, DI exchange, labor — and every future proposal
 * prices itself. Supports CSV import of a vendor sheet and CSV export.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, Save, Trash2, X, BookOpen, Search, Upload, Download,
  Sparkles, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/App";
import { openAuthedDoc } from "@/lib/openAuthedDoc";

export const EquipmentPriceBook = ({ open, onClose, token }) => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingAll, setSavingAll] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [onlyUnpriced, setOnlyUnpriced] = useState(false);
  const fileRef = useRef(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/bid-desk/price-book`, { headers: { Authorization: `Bearer ${token}` } });
      setRows(r.data?.rows || []);
      setSummary(r.data?.summary || null);
    } catch {
      toast.error("Could not load the price book");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const edit = (id, key, value) =>
    setRows((rs) => rs.map((r) => (r._uid === id || r.id === id ? { ...r, [key]: value, _dirty: true } : r)));

  const saveRow = async (row) => {
    setSavingId(row.id || row._uid);
    try {
      const r = await axios.post(`${API}/bid-desk/price-book`, row, { headers });
      setRows((rs) => rs.map((x) => ((x.id || x._uid) === (row.id || row._uid) ? { ...r.data.row } : x)));
      toast.success("Price saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that row");
    } finally { setSavingId(null); }
  };

  const saveAll = async () => {
    const dirty = rows.filter((r) => r._dirty && (r.description || "").trim());
    if (!dirty.length) { toast.info("Nothing changed yet"); return; }
    setSavingAll(true);
    try {
      const r = await axios.post(`${API}/bid-desk/price-book/bulk`, { rows: dirty }, { headers });
      toast.success(`${r.data.saved} row(s) saved`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSavingAll(false); }
  };

  const addRow = () => setRows((rs) => [
    { _uid: `new-${Date.now()}`, key: "", category: cat === "all" ? "Other" : cat, description: "",
      uom: "Each", unit_cost: 0, unit_price: 0, lead_time: "", vendor: "", sku: "", notes: "", _dirty: true },
    ...rs,
  ]);

  const addStarters = async () => {
    try {
      const r = await axios.post(`${API}/bid-desk/price-book/starter-rows`, {}, { headers });
      toast.success(r.data.added ? `${r.data.added} starter line(s) added` : "Every starter line is already here");
      await load();
    } catch { toast.error("Could not add the starter rows"); }
  };

  const importCsv = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/bid-desk/price-book/import`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" } });
      const { created, updated, skipped, errors } = r.data;
      toast.success(`Imported — ${created} new, ${updated} updated`, {
        description: skipped ? `${skipped} row(s) had no description and were skipped` : undefined });
      if (errors?.length) toast.error(errors.join(" · "));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read that CSV");
    } finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeRow = async (row) => {
    if (!row.id) { setRows((rs) => rs.filter((x) => x._uid !== row._uid)); return; }
    if (!window.confirm(`Remove "${row.description}" from the price book?`)) return;
    try {
      await axios.delete(`${API}/bid-desk/price-book/${row.id}`, { headers });
      setRows((rs) => rs.filter((x) => x.id !== row.id));
      toast.success("Removed");
    } catch { toast.error("Could not remove that row"); }
  };

  const categories = summary?.categories || [];
  const needle = q.trim().toLowerCase();
  const visible = rows.filter((r) => {
    if (cat !== "all" && (r.category || "Other") !== cat) return false;
    if (onlyUnpriced && Number(r.unit_price) > 0) return false;
    if (!needle) return true;
    return [r.description, r.category, r.vendor, r.sku, r.key, r.notes]
      .some((v) => (v || "").toString().toLowerCase().includes(needle));
  });
  const dirtyCount = rows.filter((r) => r._dirty).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-6xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()} data-testid="equipment-price-book">
        <div className="px-4 sm:px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-lg font-black text-slate-900 inline-flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-teal-600" /> Water &amp; steam equipment price book
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Key your cost and sell price once — notes-to-quote BOMs and proposals price from these rows.
            </p>
            {summary && (
              <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px]">
                <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-bold">{summary.total} lines</span>
                <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold border border-emerald-200">
                  {summary.priced} priced</span>
                {!!summary.unpriced && (
                  <button onClick={() => setOnlyUnpriced((v) => !v)}
                    className={`px-2 py-0.5 rounded font-bold border ${onlyUnpriced
                      ? "bg-amber-500 text-white border-amber-500"
                      : "bg-amber-50 text-amber-800 border-amber-200"}`}
                    data-testid="price-book-unpriced-filter">
                    <AlertTriangle className="w-3 h-3 inline -mt-0.5 mr-1" />
                    {summary.unpriced} still at $0
                  </button>
                )}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 shrink-0"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-4 sm:px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search description, vendor, SKU…"
              className="h-9 pl-8 text-xs" data-testid="price-book-search" />
          </div>
          <select value={cat} onChange={(e) => setCat(e.target.value)}
            className="h-9 text-xs border border-slate-300 rounded-md px-2 bg-white"
            data-testid="price-book-category">
            <option value="all">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={addRow} data-testid="price-book-add">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add row
          </Button>
          <Button size="sm" variant="outline" onClick={addStarters} data-testid="price-book-starters">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Starter lines
          </Button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
            onChange={(e) => importCsv(e.target.files?.[0])} data-testid="price-book-file" />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="price-book-import">
            <Upload className="w-3.5 h-3.5 mr-1" /> Import CSV
          </Button>
          <Button size="sm" variant="outline" data-testid="price-book-export"
            onClick={() => openAuthedDoc("/bid-desk/price-book/export", { token, fmt: "csv", filename: "SterileMate-price-book.csv" })}>
            <Download className="w-3.5 h-3.5 mr-1" /> Export
          </Button>
          <Button size="sm" onClick={saveAll} disabled={savingAll || !dirtyCount}
            className="bg-slate-900 hover:bg-slate-800" data-testid="price-book-save-all">
            {savingAll ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Save{dirtyCount ? ` ${dirtyCount}` : ""}
          </Button>
        </div>

        <div className="p-3 sm:p-4 overflow-auto">
          {loading ? (
            <p className="text-slate-500 inline-flex items-center gap-2 py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
          ) : (
            <table className="w-full min-w-[1040px] text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  {["Category", "Description", "Vendor / SKU", "UOM", "Cost $", "Sell $", "Lead time", ""].map((h) => (
                    <th key={h} className="text-left px-2 py-2 text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visible.map((r) => {
                  const rid = r.id || r._uid;
                  const unpriced = !(Number(r.unit_price) > 0);
                  return (
                    <tr key={rid} className={r._dirty ? "bg-amber-50/60" : unpriced ? "bg-slate-50/40" : ""}
                      data-testid={`price-row-${r.key || rid}`}>
                      <td className="px-2 py-1.5 w-36">
                        <Input value={r.category || ""} onChange={(e) => edit(rid, "category", e.target.value)} className="h-8 text-xs" />
                      </td>
                      <td className="px-2 py-1.5 min-w-[260px]">
                        <Input value={r.description || ""} onChange={(e) => edit(rid, "description", e.target.value)} className="h-8 text-xs" />
                      </td>
                      <td className="px-2 py-1.5 w-44">
                        <Input value={r.vendor || ""} onChange={(e) => edit(rid, "vendor", e.target.value)}
                          placeholder="Vendor" className="h-8 text-xs mb-1" />
                        <Input value={r.sku || ""} onChange={(e) => edit(rid, "sku", e.target.value)}
                          placeholder="SKU" className="h-8 text-xs" />
                      </td>
                      <td className="px-2 py-1.5 w-20">
                        <Input value={r.uom || ""} onChange={(e) => edit(rid, "uom", e.target.value)} className="h-8 text-xs" />
                      </td>
                      <td className="px-2 py-1.5 w-28">
                        <Input type="number" value={r.unit_cost ?? 0} onChange={(e) => edit(rid, "unit_cost", e.target.value)}
                          className="h-8 text-xs text-right" data-testid={`price-cost-${r.key || rid}`} />
                      </td>
                      <td className="px-2 py-1.5 w-28">
                        <Input type="number" value={r.unit_price ?? 0} onChange={(e) => edit(rid, "unit_price", e.target.value)}
                          className={`h-8 text-xs text-right font-semibold ${unpriced ? "border-amber-300" : ""}`}
                          data-testid={`price-sell-${r.key || rid}`} />
                      </td>
                      <td className="px-2 py-1.5 w-28">
                        <Input value={r.lead_time || ""} onChange={(e) => edit(rid, "lead_time", e.target.value)}
                          className="h-8 text-xs" placeholder="6-8 wks" />
                      </td>
                      <td className="px-2 py-1.5 w-24 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => saveRow(r)} disabled={savingId === rid}
                          className="text-emerald-700 hover:bg-emerald-50" data-testid={`price-save-${r.key || rid}`}>
                          {savingId === rid ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => removeRow(r)} className="text-red-600 hover:bg-red-50">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {!visible.length && (
                  <tr><td colSpan={8} className="px-2 py-6 text-center text-sm text-slate-400">
                    Nothing matches that filter.
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-4 sm:px-5 py-2.5 border-t border-slate-100 text-[11px] text-slate-500">
          CSV columns: key, category, description, uom, unit_cost, unit_price, lead_time, vendor, sku, notes.
          Rows match on key, then on an exact description — re-importing an updated vendor sheet updates
          prices instead of duplicating lines.
        </div>
      </div>
    </div>
  );
};

export default EquipmentPriceBook;
